/**
 * Centralized Google Maps script loader utility
 * Ensures only one script is loaded with all required libraries
 * Prevents race conditions and initialization errors
 */

declare global {
  interface Window {
    google?: {
      maps: {
        Map: new (element: HTMLElement, options: any) => any;
        Marker: new (options: any) => any;
        LatLng: new (lat: number, lng: number) => any;
        Geocoder: new () => any;
        places?: {
          Autocomplete: new (input: HTMLInputElement, options?: any) => any;
          AutocompleteService: new () => any;
          PlacesService: new (element: HTMLElement) => any;
        };
      };
    };
  }
}

interface LoadPromise {
  promise: Promise<void>;
  libraries: string[];
}

let loadPromise: LoadPromise | null = null;

/**
 * Extracts libraries from a Google Maps script URL
 */
function getLibrariesFromScript(script: HTMLScriptElement): string[] {
  const src = script.src;
  const librariesMatch = src.match(/[?&]libraries=([^&]+)/);
  if (librariesMatch) {
    return librariesMatch[1].split(',');
  }
  return [];
}

/**
 * Checks if the script has all required libraries
 */
function hasRequiredLibraries(script: HTMLScriptElement, requiredLibraries: string[]): boolean {
  if (requiredLibraries.length === 0) {
    return true; // No libraries required
  }
  const existingLibraries = getLibrariesFromScript(script);
  return requiredLibraries.every(lib => existingLibraries.includes(lib));
}

/**
 * Waits for Google Maps API to be fully loaded
 */
function waitForGoogleMaps(libraries: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait time
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (window.google && window.google.maps) {
        // Check if required libraries are available
        if (libraries.includes('places')) {
          if (!window.google.maps.places) {
            if (attempts >= maxAttempts) {
              clearInterval(checkInterval);
              reject(new Error('Google Maps Places library failed to load'));
              return;
            }
            return; // Keep waiting
          }
        }
        
        clearInterval(checkInterval);
        resolve();
        return;
      }
      
      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        reject(new Error('Google Maps API failed to load'));
      }
    }, 100);
  });
}

/**
 * Loads the Google Maps script with the specified libraries
 * @param libraries Array of library names to load (e.g., ['places'])
 * @returns Promise that resolves when Google Maps is fully loaded
 */
export function loadGoogleMapsScript(libraries: string[] = []): Promise<void> {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }

  // Check if Google Maps is already loaded
  if (window.google && window.google.maps) {
    // Check if required libraries are available
    if (libraries.includes('places') && !window.google.maps.places) {
      // Places library is required but not loaded
      // Once Google Maps is loaded, we can't reload it with different libraries
      // Return a rejected promise with a clear error
      return Promise.reject(new Error('Google Maps is already loaded without the Places library. Please reload the page or ensure Places library is included in the initial load.'));
    } else {
      // Everything is already loaded
      return Promise.resolve();
    }
  }

  // Check if there's already a loading promise
  if (loadPromise) {
    // Check if the existing promise includes our required libraries
    const existingLibraries = loadPromise.libraries;
    const needsAdditionalLibraries = libraries.some(lib => !existingLibraries.includes(lib));
    
    if (needsAdditionalLibraries) {
      // We need additional libraries, but a load is already in progress
      // Wait for the current load, then check if we need to reload
      return loadPromise.promise.then(() => {
        // Check if the loaded script has what we need
        if (libraries.includes('places') && !window.google?.maps?.places) {
          // Need to reload with places library
          // Remove the existing script and reload
          const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement;
          if (existingScript) {
            existingScript.remove();
          }
          loadPromise = null;
          return loadGoogleMapsScript(libraries);
        }
        // If we get here, the loaded script should have what we need
        return Promise.resolve();
      });
    } else {
      // The existing promise will load what we need, just wait for it
      return loadPromise.promise;
    }
  }

  // Check if script tag already exists
  const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement;
  
  if (existingScript) {
    // Script exists, check if it has required libraries
    if (hasRequiredLibraries(existingScript, libraries)) {
      // Script has what we need, just wait for it to load
      const promise = waitForGoogleMaps(libraries);
      loadPromise = { promise, libraries };
      return promise;
    } else {
      // Script exists but doesn't have required libraries
      // Remove it and clear any existing load promise to prevent conflicts
      existingScript.remove();
      loadPromise = null;
    }
  }

  // Create new loading promise
  const promise = new Promise<void>((resolve, reject) => {
    // Build libraries parameter
    const librariesParam = libraries.length > 0 ? `&libraries=${libraries.join(',')}` : '';
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${librariesParam}`;
    
    // Create script element
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Wait for Google Maps to be fully initialized
      waitForGoogleMaps(libraries)
        .then(() => {
          resolve();
        })
        .catch(reject);
    };
    
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    
    document.head.appendChild(script);
  });

  // Store the promise with the libraries it's loading
  const allLibraries = libraries.length > 0 ? libraries : [];
  loadPromise = { promise, libraries: allLibraries };
  
  return promise;
}

